import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (token) api.post("/auth/reset-link-opened", { token }).catch(() => {});
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Your password has been reset.");
      setTimeout(() => nav("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset your password.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-indigo-950/40 border border-violet-300/20 rounded-xl px-4 py-3 text-violet-50 focus:outline-none focus:border-violet-300/50 focus:ring-1 focus:ring-violet-300/40 transition-soft";

  return (
    <div className="min-h-screen relative flex items-center justify-center px-6 pt-safe-top pb-safe-bottom text-violet-50">
      <div className="relative z-10 w-full max-w-md fade-rise">
        <Link to="/" className="flex items-center gap-2.5 mb-12 justify-center" data-testid="back-to-landing-link">
          <img src="/continua-logo-small.png" alt="" aria-hidden="true" className="w-9 h-9 object-contain moon-pulse" />
          <span className="font-serif-display text-xl">Continua</span>
        </Link>

        <div className="glass-strong rounded-2xl p-8 md:p-10" data-testid="reset-password-card">
          {!token ? (
            <>
              <h1 className="font-serif-display text-3xl mb-2 leading-tight">This link is incomplete.</h1>
              <p className="text-violet-200/75 mb-8 text-sm" data-testid="reset-missing-token-message">
                The reset link seems broken. Please request a new one from the sign-in page.
              </p>
              <Link
                to="/login"
                data-testid="reset-back-to-login-link"
                className="block text-center w-full rounded-full px-6 py-3.5 bg-violet-200 text-indigo-950 font-medium hover:bg-violet-100 transition-soft glow-violet"
              >
                Back to sign in
              </Link>
            </>
          ) : done ? (
            <>
              <h1 className="font-serif-display text-3xl mb-2 leading-tight">All set.</h1>
              <p className="text-violet-200/75 text-sm" data-testid="reset-success-message">
                Your new password is saved. Taking you to sign in…
              </p>
            </>
          ) : (
            <>
              <h1 className="font-serif-display text-3xl md:text-4xl mb-2 leading-tight">A new password.</h1>
              <p className="text-violet-200/75 mb-8 text-sm">Choose something only you would remember.</p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-[0.25em] text-violet-300/70 mb-2 block">New password</label>
                  <input
                    type="password" required minLength={6} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-new-password-input"
                    className={inputCls} placeholder="••••••••"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.25em] text-violet-300/70 mb-2 block">Confirm password</label>
                  <input
                    type="password" required minLength={6} value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-confirm-password-input"
                    className={inputCls} placeholder="••••••••"
                  />
                </div>
                <button
                  type="submit" disabled={loading}
                  data-testid="reset-password-submit-button"
                  className="w-full rounded-full px-6 py-3.5 bg-violet-200 text-indigo-950 font-medium hover:bg-violet-100 transition-soft disabled:opacity-50 glow-violet"
                >
                  {loading ? "Saving…" : "Save new password"}
                </button>
              </form>
              <p className="text-sm text-violet-300/60 text-center mt-8">
                <Link to="/login" data-testid="reset-back-to-login-link" className="text-violet-200 hover:text-white">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
